import React, { useState } from 'react';
import SolarWindGauge from './SolarWindGauge';
import ISSGroundTrack from './ISSGroundTrack';
import EventTimelineRail from './EventTimelineRail';
import StellariumViewer from './charts/StellariumViewer';
import LiveFeeds from './orion/LiveFeeds';

const DOMAIN_LABELS = {
  space_weather: 'Space Weather',
  solar_activity: 'Solar Activity',
  orbital_tracking: 'Orbital Tracking',
  astronomy_events: 'Astronomy',
  cosmic_background: 'Cosmic',
};

const LEVEL_COLORS = {
  NOMINAL: '#00E5FF',
  ELEVATED: '#FF9F0A',
  STORM: '#FF6B35',
  EXTREME: '#FF3B30',
};

export default function OrionCenterPanel({ insights, offlineNotices, selectedInsightId, onSelectInsight, scoring }) {
  const [domainFilter, setDomainFilter] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [showSky, setShowSky] = useState(false);

  const allInsights = insights || [];

  const filtered = domainFilter === 'all'
    ? allInsights
    : allInsights.filter(i => i.domain === domainFilter);

  // Always reveal the strongest few even when severities are calm — the
  // previous `severity < 0.05` cutoff hid every card except the auto-selected
  // one when the day was quiet, which made the feed look broken.
  const MIN_VISIBLE = 8;
  const SEVERITY_FLOOR = 0.05;
  const visibleIds = new Set(
    filtered
      .slice()
      .sort((a, b) => (b.severity ?? 0) - (a.severity ?? 0))
      .filter((insight, idx) => showAll || insight.severity >= SEVERITY_FLOOR || idx < MIN_VISIBLE || insight.id === selectedInsightId)
      .map((insight) => insight.id),
  );
  const hiddenCount = filtered.length - visibleIds.size;

  const domains = ['all', 'space_weather', 'solar_activity', 'orbital_tracking', 'astronomy_events', 'cosmic_background'];

  return (
    <div className="orion-center" data-testid="orion-center-panel">
      {/* Live feeds with fallback rotation. */}
      <LiveFeeds />

      {/* Visual Intelligence Strip */}
      <div className="orion-vis-strip" data-testid="orion-visual-strip">
        <SolarWindGauge insights={allInsights} />
        <ISSGroundTrack insights={allInsights} />
      </div>

      {/* Live sky view — Stellarium web engine (collapsed by default) */}
      <div className="orion-sky-wrap" data-testid="orion-sky-wrap" style={{ marginBottom: 10 }}>
        <button
          className="orion-filter-btn"
          onClick={() => setShowSky(s => !s)}
          data-testid="orion-toggle-sky"
          style={{ marginBottom: 6 }}
        >
          {showSky ? 'HIDE LIVE SKY' : 'LIVE SKY (STELLARIUM)'}
        </button>
        {showSky && (
          <StellariumViewer height={420} testId="orion-stellarium-viewer" />
        )}
      </div>

      {/* Degraded monitoring strip */}
      {offlineNotices && offlineNotices.length > 0 && (
        <div className="orion-degraded-strip" data-testid="orion-degraded-strip">
          <span className="orion-degraded-icon">!</span>
          <span className="orion-degraded-text">
            {offlineNotices.length} source{offlineNotices.length > 1 ? 's' : ''} offline: {offlineNotices.map(n => n.sources?.[0] || n.domain).join(', ')}
          </span>
          {scoring?.monitoringState !== 'LIVE' && (
            <span className="orion-degraded-note">Partial assessment available</span>
          )}
        </div>
      )}

      {/* Filter bar */}
      <div className="orion-filter-bar" data-testid="orion-domain-filter">
        {domains.map(d => (
          <button
            key={d}
            className={`orion-filter-btn ${domainFilter === d ? 'active' : ''}`}
            onClick={() => setDomainFilter(d)}
            data-testid={`filter-${d}`}
          >
            {d === 'all' ? 'ALL' : DOMAIN_LABELS[d]?.toUpperCase() || d.toUpperCase()}
          </button>
        ))}
        <span className="orion-filter-count">{filtered.length}</span>
        <button
          className={`orion-filter-btn ${showAll ? 'active' : ''}`}
          onClick={() => setShowAll(!showAll)}
          style={{ marginLeft: 'auto' }}
          data-testid="filter-show-all"
        >
          {showAll ? 'FILTERED' : 'SHOW ALL'}
        </button>
      </div>

      {/* Insights feed */}
      <div className="orion-feed" data-testid="orion-insights-feed">
        {filtered.length === 0 && (
          <div className="orion-empty">No active signals in this domain</div>
        )}
        {filtered.map(insight => {
          const alertColor = LEVEL_COLORS[insight.alert] || '#00E5FF';
          const isSelected = insight.id === selectedInsightId;
          if (!visibleIds.has(insight.id)) return null;
          const isQuiet = (insight.severity ?? 0) < SEVERITY_FLOOR && !isSelected;
          return (
            <div
              key={insight.id}
              className={`orion-insight-card ${isSelected ? 'selected' : ''} ${isQuiet ? 'quiet' : ''}`}
              onClick={() => onSelectInsight(insight.id)}
              style={{ borderLeftColor: alertColor, opacity: isQuiet ? 0.72 : 1 }}
              data-testid={`insight-card-${insight.id}`}
            >
              <div className="orion-ic-header">
                <span className="orion-ic-badge" style={{ color: alertColor, borderColor: `${alertColor}40`, background: `${alertColor}10` }}>
                  {insight.alert}
                </span>
                <span className="orion-ic-domain">{DOMAIN_LABELS[insight.domain] || insight.domain}</span>
                <span className="orion-ic-conf">{(insight.confidence * 100).toFixed(0)}%</span>
              </div>
              <div className="orion-ic-topic">{insight.topic}</div>
              <div className="orion-ic-meta">
                <span className="orion-ic-sources">{insight.sources?.join(', ')}</span>
              </div>
            </div>
          );
        })}
        {!showAll && hiddenCount > 0 && (
          <button
            type="button"
            className="orion-filter-btn"
            onClick={() => setShowAll(true)}
            data-testid="orion-feed-show-hidden"
            style={{ alignSelf: 'flex-start', marginTop: 6 }}
          >
            + SHOW {hiddenCount} CALM SIGNAL{hiddenCount === 1 ? '' : 'S'}
          </button>
        )}
      </div>

      {/* Timeline Rail */}
      <EventTimelineRail insights={filtered} selectedInsightId={selectedInsightId} onSelectInsight={onSelectInsight} />
    </div>
  );
}
